import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { normalizeLang, translate } from '../../i18n/messages';
import styles from './auth.module.css';

const LANG_KEY = 'teuxdeux.lang';

function initialLang() {
  try {
    return normalizeLang(localStorage.getItem(LANG_KEY) || document.documentElement.lang);
  } catch {
    return 'en';
  }
}

export function AuthScreen({ waiting = false, boardError = false, onRetry }) {
  const { status, notice, signIn, signUp } = useAuth();
  const [lang, setLang] = useState(initialLang);
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const t = (key) => translate(lang, key);

  const applyLang = (next) => {
    const value = normalizeLang(next);
    setLang(value);
    document.documentElement.lang = value;
    document.documentElement.dir = value === 'ar' ? 'rtl' : 'ltr';
    try {
      localStorage.setItem(LANG_KEY, value);
    } catch {
      /* ignore */
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('invalid_email');
      return;
    }
    if (!password || password.length > 128 || (mode === 'signup' && password.length < 10)) {
      setError('weak_password');
      return;
    }
    if (mode === 'signup' && (name.trim().length < 1 || name.trim().length > 80)) {
      setError('invalid_name');
      return;
    }
    setPending(true);
    setError('');
    const result =
      mode === 'signup'
        ? await signUp(cleanEmail, password, name.trim())
        : await signIn(cleanEmail, password);
    if (!result.ok) {
      const code =
        result.body?.error || (!result.status || result.status >= 500 ? 'network' : 'server');
      setError(translate(lang, `auth.${code}`) === `auth.${code}` ? 'server' : code);
    }
    setPending(false);
  };

  if (waiting || status === 'loading') {
    return (
      <main className={styles.screen} aria-busy="true">
        <div className={styles.bar} />
      </main>
    );
  }

  return (
    <main className={styles.screen}>
      <div className={styles.bar} />
      <div className={styles.lang} role="radiogroup" aria-label={t('prefs.language')}>
        <button type="button" role="radio" aria-checked={lang === 'en'} onClick={() => applyLang('en')}>
          {t('lang.enShort')}
        </button>
        <button type="button" role="radio" aria-checked={lang === 'ar'} onClick={() => applyLang('ar')}>
          {t('lang.arShort')}
        </button>
      </div>
      <div className={styles.card}>
        <p className={styles.word}>ZIPPAX</p>
        {boardError ? (
          <>
            <h1 className={styles.title}>{t('auth.signIn')}</h1>
            <p className={styles.error} role="alert">{t('auth.network')}</p>
            <button className={styles.submit} type="button" onClick={onRetry}>
              {t('auth.retry')}
            </button>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <h1 className={styles.title}>{mode === 'signup' ? t('auth.create') : t('auth.signIn')}</h1>
            {mode === 'signup' && (
              <label className={styles.field}>
                <span>{t('auth.name')}</span>
                <input
                  value={name}
                  autoComplete="name"
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
            )}
            <label className={styles.field}>
              <span>{t('auth.email')}</span>
              <input
                type="email"
                value={email}
                autoComplete="username"
                autoFocus
                spellCheck={false}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span>{t('auth.password')}</span>
              <input
                type="password"
                value={password}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            {mode === 'signup' && <p className={styles.hint}>{t('auth.passwordHint')}</p>}
            {(error || notice) && (
              <p className={styles.error} role="alert">
                {t(`auth.${error || notice}`)}
              </p>
            )}
            <button className={styles.submit} type="submit" disabled={pending}>
              {pending ? t('auth.working') : mode === 'signup' ? t('auth.submitUp') : t('auth.submitIn')}
            </button>
            <p className={styles.switch}>
              {mode === 'signup' ? t('auth.switchToIn') : t('auth.switchToUp')}{' '}
              <button
                type="button"
                onClick={() => {
                  setMode(mode === 'signup' ? 'signin' : 'signup');
                  setError('');
                  setPassword('');
                }}
              >
                {mode === 'signup' ? t('auth.switchIn') : t('auth.switchUp')}
              </button>
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
